import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { instructorApi } from "../../services/instructorApi";
import { statusBadgeClass } from "../../components/instructor/InstructorShell";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";
import { useAuth } from "../../context/AuthContext";

export default function AdminApplicationsPage() {
  const { signOut, user } = useAuth();
  const [status, setStatus] = useState("");
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await instructorApi.adminList(status || undefined);
        if (!cancelled) setApplications(data.applications || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Admin Applications"
        description="Review instructor applications."
        canonicalPath="/admin/applications"
        robots="noindex,nofollow"
      />
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-semibold text-gray-900">Admin · Applications</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/instructor" className="text-emerald-700 hover:underline">
              Instructor
            </Link>
            <Link to="/admin/partner-applications" className="text-emerald-700 hover:underline">
              Partners
            </Link>
            <span className="text-gray-500">{user?.email}</span>
            <button type="button" onClick={() => signOut()} className="text-gray-600">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Review queue</h1>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">All actionable</option>
            <option value="Submitted">Submitted</option>
            <option value="UnderReview">Under Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Instructor</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{app.display_name || "Untitled"}</div>
                      <div className="text-gray-500">{app.email}</div>
                      <div className="text-gray-400 text-xs">
                        {[app.city, app.country].filter(Boolean).join(", ")} · {app.credential_count} certs ·{" "}
                        {app.offering_count} offerings
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(app.status)}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{app.completeness_score}%</td>
                    <td className="px-4 py-3 text-gray-500">
                      {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/applications/${app.id}`}
                        className="text-emerald-700 font-medium hover:underline"
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
                {!applications.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No applications in this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
